import { useState } from 'react';
import { Icon } from '@iconify/react';
import { useSaveAiWorkerUsageMutation } from '@circuitry-ai/doc-data';
import { formatAgentRunDuration } from '@circuitry-ai/doc-ui';
import { v4 as uuidv4 } from 'uuid';

interface ActionEntry {
  output: string | null;
  node_name: string;
  aiworker_usage_id: string;
}

interface HITLWorkflowRendererProps {
  claimData: any[];
  feedbackComponent?: React.ReactNode;
  onRefresh?: () => void;
}

const HITL_ACTIONS = [
  { key: 'approve',           label: 'Approve',           icon: 'mdi:check',               cls: 'bg-success hover:bg-success text-white border-success'        },
  { key: 'reject',            label: 'Reject',             icon: 'mdi:close',               cls: 'bg-danger hover:bg-danger text-white border-danger'              },
  { key: 'escalate',          label: 'Escalate',           icon: 'mdi:arrow-up',            cls: 'bg-warning hover:bg-warning text-white border-warning'     },
  { key: 'modify',            label: 'Modify',             icon: 'mdi:pencil',              cls: 'bg-primary hover:bg-primary text-white border-primary'           },
  { key: 'request_more_info', label: 'Request Info',       icon: 'mdi:information-outline', cls: 'bg-default-200 hover:bg-default-300 text-default-700 border-default-300'        },
  { key: 'override',          label: 'Override',           icon: 'mdi:shield-check',        cls: 'bg-purple-600 hover:bg-purple-700 text-white border-purple-700'     },
] as const;

function formatTs(ts: string): string {
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function AgentOutputCard({ entryKey, entry }: { entryKey: string; entry: ActionEntry }) {
  const [expanded, setExpanded] = useState(false);
  const label = entry.node_name || entryKey;
  const hasOutput = entry.output !== null && entry.output !== '';
  const text = hasOutput ? String(entry.output) : null;
  const isLong = (text?.length ?? 0) > 180;
  const preview = text ? (isLong && !expanded ? text.slice(0, 180) + '…' : text) : null;

  return (
    <div className={`border rounded-lg overflow-hidden text-xs ${hasOutput ? 'border-success' : 'border-default-200'}`}>
      <div className={`flex items-center gap-2 px-3 py-2 ${hasOutput ? 'bg-success/10' : 'bg-default-100'}`}>
        <Icon
          icon={hasOutput ? 'mdi:check-circle' : 'mdi:circle-outline'}
          className={hasOutput ? 'text-success' : 'text-default-400'}
          width={12}
        />
        <span className="font-semibold text-default-600 flex-1 truncate">{label}</span>
        {isLong && (
          <button type="button" onClick={() => setExpanded(v => !v)} className="text-primary hover:text-primary font-medium flex-shrink-0">
            {expanded ? 'Less' : 'More'}
          </button>
        )}
      </div>
      {preview && (
        <div className="px-3 py-2 bg-content1">
          <pre className="whitespace-pre-wrap font-sans text-default-600 leading-relaxed break-words">{preview}</pre>
        </div>
      )}
    </div>
  );
}

export function HITLWorkflowRenderer({ claimData, feedbackComponent, onRefresh }: Readonly<HITLWorkflowRendererProps>) {
  const data = claimData?.[0];
  const [saveUsage] = useSaveAiWorkerUsageMutation();

  const [notes, setNotes]           = useState('');
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [savedAction, setSavedAction] = useState<string | null>(null);

  if (!data) return null;

  const ao: Record<string, ActionEntry> = data.action_output ?? {};
  const hitlEntry = ao.humanInTheLoop;
  const otherKeys = Object.keys(ao).filter(k => k !== 'humanInTheLoop');

  const status = (data.status ?? '').toLowerCase();
  const isPending  = status === 'in_progress' || status === 'hitl_pending' || status === 'pending';
  const isDecided  = hitlEntry?.output && hitlEntry.output !== '';
  const isComplete = status === 'completed' || status === 'succeeded';

  const statusLabel = isComplete ? 'Completed' : isPending ? 'Pending Review' : status;
  const statusCls   = isComplete
    ? 'bg-success/10 text-success border-success'
    : isPending
      ? 'bg-warning/10 text-warning border-warning'
      : 'bg-default-100 text-default-500 border-default-200';

  const handleAction = async (action: string) => {
    setSubmitting(action);
    try {
      await saveUsage({
        aiworker_id: data.aiworker_id,
        payLoad: {
          aiworker_id:       data.aiworker_id,
          aiworker_usage_id: uuidv4(),
          aiworker_type:     'asj_logs',
          used_at:           new Date().toISOString(),
          status:            `hitl_${action}`,
          input_data: {
            original_usage_id: data.aiworker_usage_id,
            human_decision:    action,
            human_notes:       notes,
          },
          output_data: { human_decision: action, human_notes: notes },
        },
      }).unwrap();
      setSavedAction(action);
      onRefresh?.();
    } catch {
      /* RTK handles toast */
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-content1">

      {/* ── Header ── */}
      <div className="px-5 py-4 border-b border-default-200 flex-shrink-0">
        <div className="flex items-start justify-between gap-2 pr-8">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-violet-50 border border-violet-200 flex items-center justify-center flex-shrink-0">
              <Icon icon="mdi:routes" className="text-violet-600" width={18} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-bold text-foreground truncate">{data.agent_name ?? data.aiworker_name ?? 'Agent'}</div>
              <div className="text-xs text-default-400 mt-0.5">{formatTs(data.timestamp ?? data.date ?? data.used_at ?? '')}</div>
            </div>
          </div>
          <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border flex-shrink-0 mt-0.5 ${statusCls}`}>
            {statusLabel}
          </span>
        </div>
      </div>

      {/* ── Scrollable body ── */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

        {/* ── Agent Outputs ── */}
        {otherKeys.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Icon icon="mdi:robot-outline" className="text-default-400" width={13} />
              <span className="text-xs font-bold text-default-500 uppercase tracking-wider">Agent Outputs</span>
            </div>
            <div className="space-y-2">
              {otherKeys.map(k => (
                <AgentOutputCard key={k} entryKey={k} entry={ao[k]} />
              ))}
            </div>
          </div>
        )}

        {/* ── Human in the Loop ── */}
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Icon icon="mdi:account-clock" className="text-warning" width={13} />
            <span className="text-xs font-bold text-default-500 uppercase tracking-wider">Human in the Loop</span>
          </div>

          <div className="border border-warning rounded-xl overflow-hidden bg-warning/10">
            {/* HITL node label */}
            <div className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-warning">
              <div className="w-7 h-7 rounded-lg bg-warning/10 border border-warning flex items-center justify-center flex-shrink-0">
                <Icon icon="mdi:account-clock" className="text-warning" width={14} />
              </div>
              <span className="text-sm font-semibold text-warning">
                {hitlEntry?.node_name ?? 'Human in the Loop'}
              </span>
            </div>

            <div className="px-4 py-3 space-y-3">
              {savedAction ? (
                /* ── Post-decision confirmation ── */
                <div className="flex items-center gap-2 text-xs text-success bg-success/10 border border-success rounded-lg px-3 py-2.5">
                  <Icon icon="mdi:check-circle" className="text-success flex-shrink-0" width={14} />
                  <span>
                    Decision recorded: <strong className="capitalize">{savedAction.replace(/_/g, ' ')}</strong>
                    {notes && <span className="text-success"> — "{notes}"</span>}
                  </span>
                </div>
              ) : isDecided ? (
                /* ── Already has a decision ── */
                <div className="flex items-center gap-2 text-xs text-default-600 bg-content1 border border-default-200 rounded-lg px-3 py-2.5">
                  <Icon icon="mdi:check-circle" className="text-success flex-shrink-0" width={14} />
                  <span>Decision: <strong>{String(hitlEntry!.output)}</strong></span>
                </div>
              ) : (
                /* ── No decision yet — always show action buttons ── */
                <>
                  <div className="flex items-center gap-1.5 text-xs text-warning bg-warning/10 border border-warning rounded-lg px-3 py-2">
                    <Icon icon="mdi:clock-outline" className="flex-shrink-0" width={12} />
                    Awaiting human decision
                  </div>
                  <textarea
                    className="w-full text-xs border border-default-200 rounded-lg px-3 py-2 resize-none bg-content1 focus:outline-none focus:ring-2 focus:ring-warning focus:border-transparent transition-colors"
                    rows={2}
                    placeholder="Add review notes (optional)…"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                  />
                  <div className="flex flex-wrap gap-1.5">
                    {HITL_ACTIONS.map(({ key, label, icon, cls }) => (
                      <button
                        key={key}
                        type="button"
                        disabled={!!submitting}
                        onClick={() => handleAction(key)}
                        className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg font-semibold border capitalize transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm ${cls}`}
                      >
                        {submitting === key
                          ? <Icon icon="mdi:loading" className="animate-spin" width={11} />
                          : <Icon icon={icon} width={11} />
                        }
                        {label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Feedback ── */}
        {feedbackComponent && <div>{feedbackComponent}</div>}

        {/* ── Run details ── */}
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Icon icon="mdi:information-outline" className="text-default-400" width={13} />
            <span className="text-xs font-bold text-default-500 uppercase tracking-wider">Run Details</span>
          </div>
          <div className="border border-default-200 rounded-xl bg-content1 overflow-hidden text-xs">
            {[
              { label: 'Run ID',   value: data.aiworker_usage_id },
              { label: 'Agent',    value: data.aiworker_id },
              { label: 'Started',  value: formatTs(data.timestamp ?? data.date ?? data.used_at ?? '') },
              ...(data.duration_ms != null ? [{ label: 'Duration', value: formatAgentRunDuration(data.duration_ms) }] : []),
            ].map((row, i, arr) => (
              <div key={row.label} className={`flex gap-3 px-3 py-2 ${i < arr.length - 1 ? 'border-b border-default-200' : ''}`}>
                <span className="text-default-400 w-16 flex-shrink-0">{row.label}</span>
                <span className="text-default-600 font-mono break-all">{row.value}</span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
